import { Building2, CheckCircle, Clock, CreditCard, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { QUOTE_INPUT_KEY, buildQuoteRequest, summariseQuote } from '@/lib/orderQuote';
import { OWN_IXBRL_ID, OWN_IXBRL_SERVICE } from '@/lib/ownIxbrl';
import { formatRegionMoneyMinor, getActiveRegionConfig } from '@/lib/regionConfig';
import { TAX_COMPUTATION_ID, TAX_COMPUTATION_SERVICE, orderedServiceIds } from '@/lib/taxComputation';
import { useOrderQuote } from '@/lib/useOrderQuote';

// Services that cost nothing: they are on the order but add no line to the quote.
const FREE_SERVICES = { [TAX_COMPUTATION_ID]: TAX_COMPUTATION_SERVICE, [OWN_IXBRL_ID]: OWN_IXBRL_SERVICE };

const OrderSummarySidebar = ({ wizardData, currentStep }) => {
  const navigate = useNavigate();
  const regionConfig = getActiveRegionConfig();
  // Every figure comes from the back end's quote, which prices with the same code as the invoice.
  const request = buildQuoteRequest(wizardData);
  const { quote, loading, error } = useOrderQuote(request);

  if (!wizardData) return null;

  const money = (pence) => formatRegionMoneyMinor(pence, regionConfig);
  const summary = summariseQuote(quote);
  const companies = wizardData.selectedCompanies || [];
  const freeServices = orderedServiceIds(wizardData).filter((id) => FREE_SERVICES[id]);

  const openQuote = () => {
    sessionStorage.setItem(QUOTE_INPUT_KEY, JSON.stringify({
      optionalServices: orderedServiceIds(wizardData),
      config: wizardData.config,
      selectedCompanies: companies,
    }));
    navigate('/wizard/quote');
  };

  const lineRows = (lines) => lines.map((line) => (
    <div key={line.label} className="flex justify-between text-sm gap-3">
      <div>
        <div>{line.label}</div>
        {line.count > 1 && <div className="text-xs text-muted-foreground">{line.count} x {money(line.unit)}</div>}
      </div>
      <span className="font-medium whitespace-nowrap">{money(line.amount)}</span>
    </div>
  ));

  return (
    <div className="bg-card border border-border rounded-xl p-4 sticky top-4 shadow-card">
      <h3 className="font-semibold font-heading mb-4 flex items-center gap-2">
        <CreditCard className="w-4 h-4" />
        Order Summary
      </h3>

      <div className="space-y-2 mb-4 pb-4 border-b border-border">
        {['Options', 'Companies', 'Review', 'Upload'].map((step, idx) => (
          <div key={step} className="flex items-center gap-2 text-sm">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
              idx + 1 <= currentStep ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
              {idx + 1 < currentStep ? <CheckCircle className="w-3 h-3" /> : idx + 1}
            </div>
            <span className={idx + 1 <= currentStep ? 'font-medium' : 'text-muted-foreground'}>
              {step}
            </span>
          </div>
        ))}
      </div>

      {!request && (
        <p className="mb-4 pb-4 border-b border-border text-xs text-muted-foreground" data-testid="summary-nothing-to-price">
          Choose an account type to see the price.
        </p>
      )}

      {summary.tagging.length > 0 && (
        <div className="mb-4 pb-4 border-b border-border" data-testid="summary-tagging">
          <p className="text-xs text-muted-foreground mb-2">iXBRL Tagging Services</p>
          <div className="space-y-1">{lineRows(summary.tagging)}</div>
        </div>
      )}

      {(summary.filing.length > 0 || freeServices.length > 0) && (
        <div className="mb-4 pb-4 border-b border-border" data-testid="summary-filing">
          <p className="text-xs text-muted-foreground mb-2">{regionConfig.filingServicesLabel || 'Filing Services'}</p>
          <div className="space-y-1">
            {lineRows(summary.filing)}
            {freeServices.map((id) => (
              <div key={id} className="flex justify-between text-sm text-muted-foreground gap-3">
                <span>{FREE_SERVICES[id].title}</span>
                <span className="font-medium whitespace-nowrap">No charge</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.express && (
        <div className="mb-4 pb-4 border-b border-border" data-testid="summary-express">
          <div className="space-y-1">{lineRows([{ ...summary.express, label: 'Express delivery' }])}</div>
        </div>
      )}

      {companies.length > 0 && (
        <div className="mb-4 pb-4 border-b border-border flex items-center justify-between text-sm" data-testid="summary-company-count">
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-muted-foreground" />
            Companies
          </span>
          <span className="font-mono font-medium">{companies.length}</span>
        </div>
      )}

      {wizardData.config && (
        <div className="mb-4 pb-4 border-b border-border">
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span>{regionConfig.delivery[wizardData.config.deliveryTime]?.label || regionConfig.delivery.standard.label}</span>
          </div>
        </div>
      )}

      {error && (
        <p className="mb-4 text-xs text-red-700" data-testid="summary-error">Could not work out the price. {error}</p>
      )}

      {quote && summary.discount > 0 && (
        <div className="mb-3 space-y-1 text-sm" data-testid="summary-discount">
          <div className="flex justify-between text-muted-foreground">
            <span>Subtotal</span>
            <span className="whitespace-nowrap">{money(summary.subtotal)}</span>
          </div>
          <div className="flex justify-between text-green-700">
            <span>Volume discount ({summary.discountPercent}%)</span>
            <span className="whitespace-nowrap">-{money(summary.discount)}</span>
          </div>
        </div>
      )}

      {(quote || loading) && (
        <div className="bg-primary text-primary-foreground rounded-lg p-3" data-testid="summary-total">
          <div className="flex justify-between items-center gap-3">
            <div>
              <p className="text-xs opacity-80">Total</p>
              {companies.length > 0 && (
                <p className="text-xs opacity-60">{companies.length} {companies.length === 1 ? 'company' : 'companies'}</p>
              )}
            </div>
            <p className="text-xl font-bold font-heading">{quote ? money(summary.total) : '...'}</p>
          </div>
        </div>
      )}

      <Button variant="outline" className="w-full mt-4" onClick={openQuote} data-testid="get-price-quote-btn">
        <FileText className="w-4 h-4 mr-2" />
        Get Price Quote
      </Button>
    </div>
  );
};

export default OrderSummarySidebar;
